// Pure helpers for dashboard grid column layouts. Kept free of imports and store
// state so the reconciliation rules can be reasoned about (and exercised) on their own.

export function configurableKeys(allColumns) {
  return allColumns.filter((c) => !c.fixed).map((c) => c.key);
}

export function defaultPrefs(allColumns) {
  return configurableKeys(allColumns).map((key, order) => ({ key, visible: true, order }));
}

/**
 * Reconcile a saved layout against the packaged column catalog.
 *
 * Keys that no longer exist are dropped. Keys the user has never seen are inserted
 * directly after the nearest preceding catalog column they already have, so a newly
 * shipped column lands where it was designed to sit (Assigned after Status, before
 * Tags) instead of being appended past the far right of the grid.
 *
 * A column the user has explicitly placed is never moved — only genuinely new keys
 * are positioned.
 */
export function mergeWithDefaults(saved, allColumns) {
  const defaultKeys = configurableKeys(allColumns);
  const known = new Set(defaultKeys);

  const savedByKey = new Map();
  for (const entry of Array.isArray(saved) ? saved : []) {
    if (!entry || typeof entry.key !== "string") continue;
    if (!known.has(entry.key) || savedByKey.has(entry.key)) continue;
    const order = Number(entry.order);
    savedByKey.set(entry.key, {
      key: entry.key,
      visible: entry.visible !== false,
      order: Number.isFinite(order) ? order : Number.MAX_SAFE_INTEGER,
    });
  }

  const sequence = [...savedByKey.values()]
    .sort((a, b) => a.order - b.order)
    .map(({ key, visible }) => ({ key, visible }));

  defaultKeys.forEach((key, defaultIndex) => {
    if (savedByKey.has(key)) return;
    // Walk backwards through the catalog for the closest column already on screen;
    // with none (this is the new leading column) it goes to the front.
    let insertAt = 0;
    for (let i = defaultIndex - 1; i >= 0; i--) {
      const anchor = sequence.findIndex((entry) => entry.key === defaultKeys[i]);
      if (anchor !== -1) {
        insertAt = anchor + 1;
        break;
      }
    }
    sequence.splice(insertAt, 0, { key, visible: true });
  });

  return sequence.map((entry, order) => ({ ...entry, order }));
}

/** Visible configurable columns in saved order, with fixed columns pinned to the end. */
export function resolveColumns(prefs, allColumns) {
  const fixedCols = allColumns.filter((c) => c.fixed);
  const configurable = prefs
    .filter((p) => p.visible)
    .sort((a, b) => a.order - b.order)
    .map((p) => allColumns.find((c) => c.key === p.key))
    .filter(Boolean);
  return [...configurable, ...fixedCols];
}
